/**
 * LCOE display helpers — prefix approximate values and surface data provenance.
 *
 * All on-screen LCOE dollar figures are estimates (ranges, midpoints, or
 * site-adjusted baselines). Use `≈` consistently so readers don't treat
 * them as audited quotes.
 */
import type { SiteRagReport } from '../types/siteRag';

/** Unicode "approximately equal" — preferred over ~ for cost estimates. */
export const LCOE_APPROX_SYMBOL = '≈';

export function formatApproxLcoePerMwh(usd: number): string {
  return `${LCOE_APPROX_SYMBOL}$${Math.round(usd)}/MWh`;
}

export function formatApproxLcoeRange(low: number, high: number): string {
  return `${LCOE_APPROX_SYMBOL}$${Math.round(low)}–$${Math.round(high)}/MWh`;
}

/**
 * Prefix ≈ on authored lcoeRange strings ($45-80, pending, etc.).
 * Idempotent when the string already starts with ≈ or ~.
 */
export function formatApproxLcoeAuthored(lcoeRange: string | undefined | null): string {
  if (!lcoeRange?.trim()) return '—';
  const trimmed = lcoeRange.trim();
  if (trimmed.startsWith(LCOE_APPROX_SYMBOL) || trimmed.startsWith('~')) return trimmed;
  if (trimmed === '—' || /^pending$/i.test(trimmed)) return trimmed;
  if (/[$€£]|\d/.test(trimmed)) return `${LCOE_APPROX_SYMBOL}${trimmed}`;
  return `${LCOE_APPROX_SYMBOL}${trimmed}`;
}

export interface LcoeDataProvenance {
  sources: string[];
  asOfDate: string | null;
  sourcesLine: string;
  asOfLine: string;
  footnote: string;
}

/** Resolve where LCOE inputs came from and when they were frozen. */
export function resolveLcoeDataProvenance(report: SiteRagReport): LcoeDataProvenance {
  const sources = new Set<string>(['NREL ATB / DOE–EPRI baseline LCOE ranges']);

  const ds = report.datasets;
  if (ds?.solar?.ghi != null) sources.add('NREL solar resource (GHI)');
  if (ds?.wind?.capacityFactor != null) sources.add('NREL wind resource (capacity factor)');
  if (ds?.electricity?.retailRate != null) sources.add('EIA retail electricity rate');
  // Seismic design category is not a power LCOE source

  if (report.btmDetailedAnalysis?.length) {
    sources.add('report-authored BTM cost analysis');
  }
  if (report.lcoeDetailedAnalysis?.length) {
    sources.add('report LCOE breakdown');
  }

  const asOfDate = report.createdAt
    ? new Date(report.createdAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    : null;

  const sourcesLine = `Sources: ${[...sources].join(' · ')}.`;
  const asOfLine = asOfDate
    ? `Last accurate at report generation (${asOfDate}).`
    : 'Last accurate at report generation.';

  return {
    sources: [...sources],
    asOfDate,
    sourcesLine,
    asOfLine,
    footnote: `${sourcesLine} ${asOfLine}`,
  };
}
